"use client"

import { usePathname } from "next/navigation"
import clsx from "clsx"
import { encode } from "qss"

import type { Nullish, PropsWithClassName } from "~/@types/generics"
import { SORT_BY_PARAM } from "~/lib/algolia/constants"
import { Link, type LinkProps } from "~/components/ui/Link"
import { getDeltas } from "~/components/ui/Pagination/utils/get-deltas"
import Icon from "~/components/abstracts/Icon"
import { QUERY_PARAM_KEY, useAlgoliaCustomContext } from "~/providers/AlgoliaCustomContext/index.client"
import { applySearchParams } from "~/utils/apply-searchparams"
import { objectEntries } from "~/utils/object-entries"

import { sprinkles } from "~/styles/sprinkles.css"

import * as css from "./styles.css"

const ARROW_SIZE = 6
const FIRST_PAGE_NUMBER = 1

export type PaginationProps = PropsWithClassName<{
  disabledLinks?: boolean
  maxPage: number
  hasNextPage: Nullish<boolean>
  hasPreviousPage: Nullish<boolean>
  currentPage: number
  onPageChange?: (newPage: number) => void
}>

function Pagination({
  className,
  currentPage,
  disabledLinks,
  hasNextPage,
  hasPreviousPage,
  maxPage,
  onPageChange,
}: PaginationProps) {
  const { query, selectedFilters, sortBy } = useAlgoliaCustomContext()
  const pathname = usePathname()

  const { BEFORE_CURRENT_PAGE_DELTA, AFTER_CURRENT_PAGE_DELTA } = getDeltas(currentPage, maxPage)

  function buildHref(page: number) {
    const processedUrlParams = objectEntries(selectedFilters ?? {})?.reduce(
      (acc, [facet, values]) => {
        const truthyValues = values?.filter(Boolean) ?? []

        if (!truthyValues?.length) return acc

        return {
          ...acc,
          [facet]: [truthyValues?.filter(Boolean)?.join(",")],
        }
      },
      { page, ...(sortBy !== "default" && { [SORT_BY_PARAM]: sortBy }), ...(query && { [QUERY_PARAM_KEY]: query }) }
    )

    return applySearchParams(
      pathname,
      // we are enforcing some characters so they are not encoded, and understood by nuqs and its formatter
      // in our case, we want to replace spaces by +
      encode(processedUrlParams).replaceAll("%20", "+")
    )
  }

  function getLinkProps(page: number): LinkProps {
    if (disabledLinks) {
      return {
        onClick: (e) => {
          e.preventDefault()
          onPageChange?.(page)
        },
      }
    } else {
      return { href: buildHref(page) }
    }
  }

  return (
    <nav className={clsx(className, css.Pagination)} data-comp="UI/Pagination">
      <ul className={css.PaginationWrapper}>
        {currentPage - 1 > BEFORE_CURRENT_PAGE_DELTA && hasPreviousPage && (
          <li className={sprinkles({ display: "inline-flex" })}>
            <Link className={sprinkles({ cursor: "pointer" })} {...getLinkProps(currentPage - 1)}>
              <Icon theme="black" width={ARROW_SIZE} name="ArrowSliderLeft" />
            </Link>
          </li>
        )}
        {currentPage > FIRST_PAGE_NUMBER + BEFORE_CURRENT_PAGE_DELTA && (
          <>
            <li>
              <Link
                className={clsx(css.LinkVariants["inactive"], sprinkles({ cursor: "pointer" }))}
                {...getLinkProps(1)}
              >
                <span>1</span>
              </Link>
            </li>
            <li className={clsx(css.dots)}>...</li>
          </>
        )}
        {Array.from(Array(BEFORE_CURRENT_PAGE_DELTA)).map((_, index) => {
          const page = currentPage - (BEFORE_CURRENT_PAGE_DELTA - index)

          return (
            currentPage > 1 &&
            page > 0 && (
              <li className={clsx()} key={`pagination-${page}-${currentPage}`}>
                <Link className={css.LinkVariants["inactive"]} {...getLinkProps(page)}>
                  {page}
                </Link>
              </li>
            )
          )
        })}

        <li className={clsx()}>
          <Link className={clsx(css.LinkVariants["active"])}>
            <span>{currentPage}</span>
          </Link>
        </li>

        {Array.from({ length: AFTER_CURRENT_PAGE_DELTA }).map((_, index) => {
          const page = currentPage + index + 1

          return (
            currentPage < maxPage &&
            page < maxPage + 1 && (
              <li className={clsx()} key={`pagination-${page}-${currentPage}`}>
                <Link className={css.LinkVariants["inactive"]} {...getLinkProps(page)}>
                  {page}
                </Link>
              </li>
            )
          )
        })}
        {currentPage < maxPage - AFTER_CURRENT_PAGE_DELTA && (
          <>
            <li className={clsx(css.dots)}>...</li>
            <li className={clsx()}>
              <Link className={css.LinkVariants["inactive"]} {...getLinkProps(maxPage)}>
                <span>{maxPage}</span>
              </Link>
            </li>
          </>
        )}
        {currentPage < maxPage - AFTER_CURRENT_PAGE_DELTA && hasNextPage && (
          <li className={clsx(sprinkles({ display: "inline-flex" }))}>
            <Link className={sprinkles({ cursor: "pointer" })} {...getLinkProps(currentPage + 1)}>
              <Icon theme="black" width={ARROW_SIZE} name="ArrowSliderRight" />
            </Link>
          </li>
        )}
      </ul>
    </nav>
  )
}

export default Pagination
